import { Link } from "react-router-dom";
import Seo from "@/lib/seo/Seo";
import { getSeoForPath } from "@/lib/seo/routes.mjs";
import {
  CreditCard,
  Wallet,
  Repeat,
  Globe,
  Smartphone,
  Monitor,
  Lock,
  ArrowRight,
  ChevronRight,
  Check,
  X,
  Gift,
  Wifi,
  Landmark,
  Store,
  Hotel,
  Music,
  Utensils,
  ShoppingCart,
  Wrench,
  Scissors,
  Fuel,
  Stethoscope,
  TrendingUp,
  AlertTriangle,
  Banknote,
} from "lucide-react";
import CTASection from "@/components/CTASection";
import Reveal from "@/components/Reveal";
import ParticleField from "@/components/ParticleField";
import { AnywhereIllustration } from "@/components/illustrations/FintechIllustrations";

const PAYMENT_METHODS = [
  { icon: Smartphone, title: "Mobile Payments",          text: "Accept payments beyond the counter with flexible mobile payment solutions for businesses on the move." },
  { icon: Wallet,     title: "Digital Wallets",          text: "Support modern payment options like Apple Pay, Google Pay, and other digital wallet methods." },
  { icon: Landmark,   title: "ACH Payments",             text: "Offer bank-based payment options for qualifying transactions and business payment needs." },
  { icon: Gift,       title: "Gift Card Payments",       text: "Give businesses the ability to support gift card programs that encourage repeat customer visits." },
  { icon: Wifi,       title: "NFC Contactless Payments", text: "Speed up checkout with secure tap-to-pay technology and contactless card acceptance." },
  { icon: CreditCard, title: "Dip / Tap / Swipe",        text: "Accept a variety of card-present payment methods to match how your customers prefer to pay." },
];

const INDUSTRIES = [
  { icon: Store,       label: "Retail",           to: "/industries/retail-stores" },
  { icon: Hotel,       label: "Hospitality" },
  { icon: Music,       label: "Entertainment" },
  { icon: Utensils,    label: "Restaurant",       to: "/industries/restaurants" },
  { icon: ShoppingCart,label: "Grocery",          to: "/industries/grocery-stores" },
  { icon: Wrench,      label: "Repair Services",  to: "/industries/service-businesses" },
  { icon: Scissors,    label: "Personal Care",    to: "/industries/salons-spas" },
  { icon: Fuel,        label: "Gas Stations" },
  { icon: Globe,       label: "E-Commerce" },
  { icon: Stethoscope, label: "Health Care" },
];

const NETWORKS = [
  "American Express", "Visa", "Mastercard", "Discover", "Apple Pay",
  "PayPal", "JCB", "UnionPay", "eCheck", "ACH Payments",
];

const services = [
  {
    id: "ecommerce",
    icon: Globe,
    title: "E-Commerce Payments",
    desc: "Accept payments online with secure e-commerce processing solutions designed for websites, online stores, custom checkout pages, and digital payment experiences. Swipetronics helps businesses process online transactions safely while keeping checkout simple for customers.",
    bullets: ["Secure online checkout", "Hosted payment pages", "Website and cart integrations", "Tokenized customer profiles", "3D Secure support"],
  },
  {
    id: "mobile-payments",
    icon: Smartphone,
    title: "Mobile Payments",
    desc: "Take payments wherever your business operates — at the table, on the sales floor, during deliveries, at events, or out in the field. Mobile payment solutions give your business more flexibility while keeping transactions fast and convenient.",
    bullets: ["Wireless terminals", "Phone and tablet readers", "Tap-to-pay options", "Apple Pay and Google Pay", "On-the-go payment tools"],
  },
  {
    id: "virtual-terminal",
    icon: Monitor,
    title: "Virtual Terminal",
    desc: "Accept card-not-present payments from any browser with a secure virtual terminal. This is a strong option for phone orders, mail orders, invoices, recurring payments, and businesses that need flexible remote payment acceptance.",
    bullets: ["Web-based payment dashboard", "Keyed-in transactions", "Recurring billing", "Saved cards on file", "Refunds and voids"],
  },
  {
    id: "security",
    icon: Lock,
    title: "Security & Compliance",
    desc: "Protect your business and your customers with secure payment technology designed to support safer transactions. Swipetronics helps businesses use EMV, tokenization, PCI guidance, and chargeback support tools to reduce risk.",
    bullets: ["PCI guidance", "EMV chip protection", "Tokenization", "Secure transaction handling", "Chargeback assistance"],
  },
];

export default function Services() {
  return (
    <div data-testid="services-page">
      <Seo {...getSeoForPath("/services")} />
      {/* HERO — unified: text + illustration blended into one navy header */}
      <section className="relative bg-[var(--swipe-navy)] text-white overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 grid-faint opacity-30 pointer-events-none" />
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse 55% 65% at 78% 50%, rgba(0,114,255,0.30) 0%, transparent 65%)" }} />
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse 40% 50% at 20% 30%, rgba(0,114,255,0.14) 0%, transparent 60%)" }} />
        <div aria-hidden="true" className="absolute -top-24 -left-20 w-[420px] h-[420px] rounded-full pointer-events-none" style={{ background: "rgba(0,114,255,0.18)", filter: "blur(110px)" }} />
        <div aria-hidden="true" className="absolute -bottom-32 right-[12%] w-[480px] h-[480px] rounded-full pointer-events-none" style={{ background: "rgba(0,114,255,0.16)", filter: "blur(120px)" }} />
        <div aria-hidden="true" className="absolute top-[30%] right-[2%] w-2 h-2 rounded-full bg-[var(--swipe-accent)] opacity-70 pointer-events-none" />
        <div aria-hidden="true" className="absolute top-[18%] left-[40%] w-1.5 h-1.5 rounded-full bg-[#7DB6FF] opacity-60 pointer-events-none" />
        <div aria-hidden="true" className="absolute bottom-[20%] left-[8%] w-1.5 h-1.5 rounded-full bg-[#7DB6FF] opacity-50 pointer-events-none" />

        <ParticleField density={36} connectDistance={130} className="absolute inset-0 w-full h-full" />

        <div className="container-swipe relative pt-14 pb-16 lg:pt-20 lg:pb-24">
          <nav className="flex items-center gap-2 text-[13px] text-white/50 mb-8">
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-white/80">Card Processing</span>
          </nav>

          <div className="grid lg:grid-cols-[1.05fr,1fr] gap-10 lg:gap-12 items-center">
            <Reveal>
              <div className="relative">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/8 border border-white/15 text-[10.5px] uppercase tracking-[0.22em] font-bold text-[#7DB6FF] mb-6">
                  <span className="w-1.5 h-1.5 rounded-full bg-[var(--swipe-accent)] animate-pulse" />
                  Services
                </div>
                <h1 className="font-display-tight text-[2.75rem] sm:text-[3.75rem] lg:text-[5rem] xl:text-[5.5rem] font-extrabold leading-[1.02] text-white uppercase tracking-[-0.035em]">
                  Merchant Services &amp; <span className="text-[var(--swipe-accent)]">Card Processing</span>
                </h1>
                <p className="mt-7 text-[17px] md:text-[18px] text-white/75 leading-[1.7] max-w-xl">
                  Merchant services and credit card processing built for small businesses
                  and multi-location operations—helping you accept payments securely,
                  simplify checkout, and operate with confidence.
                </p>
                <div className="mt-9 flex flex-wrap gap-3">
                  <Link to="/savings-analysis" className="btn-primary-swipe text-[15px] py-3.5 px-7" data-testid="services-hero-cta-savings">
                    Free Savings Analysis <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link to="/contact" className="btn-outline-light text-[15px] py-3.5 px-7" data-testid="services-hero-cta-contact">
                    Talk to a Specialist
                  </Link>
                </div>
              </div>
            </Reveal>

            <Reveal variant="slide-right" delay={120}>
              <div className="relative">
                <AnywhereIllustration transparent className="relative w-full h-auto max-w-[560px] mx-auto" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* INTRO 1 — centered */}
      <section className="relative bg-white overflow-hidden">
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none opacity-60" style={{ background: "radial-gradient(ellipse 80% 60% at 50% 0%, rgba(234,243,255,0.9) 0%, transparent 70%)" }} />
        <div className="container-swipe relative pt-16 pb-12 lg:pt-20 lg:pb-16 text-center">
          <Reveal variant="words" as="h2" className="font-display-tight text-[2rem] sm:text-[2.75rem] lg:text-[3.5rem] font-extrabold text-[var(--swipe-navy)] leading-[1.08] tracking-[-0.02em] max-w-5xl mx-auto" threshold={0.05}>
            {"Payment Processing Solutions Built for Modern Businesses".split(" ").map((w, i, a) => (
              <span key={i}><span className="word"><span>{w}</span></span>{i < a.length - 1 ? " " : null}</span>
            ))}
          </Reveal>
          <Reveal variant="underline" delay={500}>
            <span aria-hidden="true" className="block mx-auto mt-6 h-[3px] w-24 rounded-full" style={{ background: "linear-gradient(90deg, #0072FF 0%, #1e90ff 100%)" }} />
          </Reveal>
          <Reveal delay={650}>
            <p className="mt-6 text-[16px] md:text-[17px] text-slate-600 leading-[1.8] max-w-[900px] mx-auto">
              Swipetronics provides modern payment processing solutions for businesses of all sizes —
              from growing local businesses to larger multi-location operations.
            </p>
          </Reveal>
        </div>
      </section>

      {/* INTRO 2 — centered, soft-blue band */}
      <section className="relative overflow-hidden" style={{ background: "linear-gradient(180deg, #FFFFFF 0%, #EAF3FF 60%, #FFFFFF 100%)" }}>
        <div className="container-swipe relative pt-14 pb-16 lg:pt-16 lg:pb-20 text-center">
          <Reveal variant="words" as="h2" className="font-display-tight text-[2rem] sm:text-[2.75rem] lg:text-[3.5rem] font-extrabold text-[var(--swipe-navy)] leading-[1.08] tracking-[-0.02em] max-w-5xl mx-auto" threshold={0.05}>
            {"Making Every Payment Possible".split(" ").map((w, i, a) => (
              <span key={i}><span className="word"><span>{w}</span></span>{i < a.length - 1 ? " " : null}</span>
            ))}
          </Reveal>
          <Reveal variant="underline" delay={500}>
            <span aria-hidden="true" className="block mx-auto mt-6 h-[3px] w-24 rounded-full" style={{ background: "linear-gradient(90deg, #0072FF 0%, #1e90ff 100%)" }} />
          </Reveal>
          <Reveal delay={650}>
            <p className="mt-6 text-[16px] md:text-[17px] text-slate-600 leading-[1.8] max-w-[900px] mx-auto">
              Whether you conduct business in person, through a mobile device, or online,
              Swipetronics provides secure, innovative payment processing services—including{" "}
              <Link
                to="/e-commerce"
                className="text-[var(--swipe-accent)] underline underline-offset-2 hover:text-[#005FE0]"
                data-testid="services-link-ecommerce"
              >
                online payment processing solutions
              </Link>
              —to help your business accept payments with confidence. Our goal is to make payment
              acceptance simple, effective, and easy to manage.
            </p>
          </Reveal>
        </div>
      </section>

      {/* CORE SERVICES — 4 colorful premium cards, 2 per row */}
      <section className="section bg-white">
        <div className="container-swipe">
          <Reveal>
            <div className="max-w-3xl mx-auto text-center mb-12">
              <div className="overline mb-3 inline-flex justify-center">Core Payment Services</div>
              <h2 className="font-display-tight text-[1.85rem] sm:text-[2.5rem] font-extrabold text-[var(--swipe-navy)] leading-[1.12]">
                Built for in-person, online, and on-the-go payments.
              </h2>
            </div>
          </Reveal>

          <div className="grid md:grid-cols-2 gap-6 lg:gap-7">
            {services.map((s, i) => {
              const Icon = s.icon;
              const accentBg = i % 2 === 0;
              return (
                <Reveal key={s.id} delay={(i % 2) * 90}>
                  <div
                    id={s.id}
                    data-testid={`service-card-${s.id}`}
                    className="group relative h-full rounded-3xl p-8 lg:p-9 border border-[var(--swipe-accent)]/15 transition-all duration-300 hover:-translate-y-1.5 hover:border-[var(--swipe-accent)] shadow-[0_10px_30px_-18px_rgba(6,26,58,0.18)] hover:shadow-[0_28px_56px_-22px_rgba(0,114,255,0.40)] overflow-hidden scroll-mt-32 flex flex-col"
                    style={{ background: accentBg ? "linear-gradient(160deg, #FFFFFF 0%, #EAF3FF 100%)" : "linear-gradient(160deg, #EAF3FF 0%, #FFFFFF 100%)" }}
                  >
                    <span aria-hidden="true" className="absolute top-0 left-0 right-0 h-[3px] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500" style={{ background: "linear-gradient(90deg, #0072FF 0%, #1e90ff 60%, #061A3A 100%)" }} />
                    <div className="absolute -top-14 -right-14 w-48 h-48 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" style={{ background: "rgba(0,114,255,0.22)", filter: "blur(44px)" }} />

                    <div className="relative flex-1 flex flex-col">
                      <div
                        className="w-14 h-14 rounded-2xl flex items-center justify-center transition-all duration-300 group-hover:scale-110 group-hover:-rotate-3"
                        style={{
                          background: accentBg
                            ? "linear-gradient(135deg, #0072FF 0%, #1e90ff 100%)"
                            : "linear-gradient(135deg, #061A3A 0%, #0A2552 100%)",
                          boxShadow: accentBg
                            ? "0 14px 28px -10px rgba(0,114,255,0.55)"
                            : "0 14px 28px -10px rgba(6,26,58,0.55)",
                        }}
                      >
                        <Icon className="w-7 h-7 text-white" strokeWidth={2.2} />
                      </div>

                      <h3 className="mt-6 font-display-tight text-[22px] lg:text-[24px] font-extrabold text-[var(--swipe-navy)] leading-tight transition-colors duration-300 group-hover:text-[var(--swipe-accent)]">
                        {s.title}
                      </h3>
                      <p className="mt-3 text-[15px] text-slate-600 leading-[1.75]">{s.desc}</p>

                      <ul className="mt-5 grid sm:grid-cols-2 gap-2.5">
                        {s.bullets.map((b) => (
                          <li key={b} className="flex items-start gap-2 text-[14px] text-slate-700">
                            <span className="mt-0.5 w-5 h-5 rounded-full bg-[var(--swipe-accent)] text-white flex items-center justify-center shrink-0">
                              <Check className="w-3 h-3" strokeWidth={3.5} />
                            </span>
                            {b}
                          </li>
                        ))}
                      </ul>

                      <div className="mt-auto pt-7">
                        <Link to="/savings-analysis" className="btn-primary-swipe text-sm py-2.5 px-5">
                          Get a Quote <ArrowRight className="w-4 h-4" />
                        </Link>
                      </div>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* PRICING COMPARISON */}
      <section className="section bg-white">
        <div className="container-swipe">
          <Reveal>
            <div className="max-w-3xl mx-auto text-center mb-12">
              <div className="overline mb-3 inline-flex justify-center">Pricing Models</div>
              <h2 className="font-display-tight text-[1.85rem] sm:text-[2.5rem] font-extrabold text-[var(--swipe-navy)] leading-[1.12]">
                Compare the two main pricing structures.
              </h2>
            </div>
          </Reveal>

          <div className="grid lg:grid-cols-2 gap-6 lg:gap-7">
            {[
              {
                icon: Banknote,
                tone: "navy",
                title: "Traditional Pricing",
                desc: "Traditional pricing is a model where the business pays the processing costs instead of passing a card-related fee to the customer.",
                pros: ["Familiar structure for many businesses", "Simple customer experience", "No card fee shown to the customer", "Good option for businesses that want standard pricing at checkout"],
                cons: ["Processing costs are paid by the business", "Monthly statements can be harder to understand if pricing is not transparent", "Can create higher monthly cost depending on volume and rates"],
              },
              {
                icon: TrendingUp,
                tone: "accent",
                title: "Cash Discount / Dual Pricing",
                desc: "Cash discount / dual pricing programs allow eligible businesses to offer clear cash and card pricing options, helping reduce the impact of card processing fees while giving customers transparent payment choices.",
                pros: [
                  "May help businesses save hundreds or even thousands on monthly processing fees",
                  "Helps offset card acceptance costs",
                  "Can improve monthly profit margins",
                  "Works well for many high-card-volume businesses",
                  "Gives customers clear cash and card pricing options",
                  "Can make payment costs more transparent",
                ],
                cons: [
                  "Not every business type may qualify",
                  "Customer signage and communication may be needed",
                  "Program availability depends on business type, processing volume, approval, and applicable requirements",
                  "Some customers may need a simple explanation at checkout",
                ],
              },
            ].map((c, i) => {
              const Icon = c.icon;
              const accentBg = c.tone === "accent";
              return (
                <Reveal key={c.title} delay={i * 100}>
                  <div
                    data-testid={`pricing-compare-${i}`}
                    className="group relative h-full rounded-3xl p-7 lg:p-9 border border-[var(--swipe-accent)]/15 transition-all duration-300 hover:-translate-y-1.5 hover:border-[var(--swipe-accent)] shadow-[0_10px_30px_-18px_rgba(6,26,58,0.18)] hover:shadow-[0_28px_56px_-22px_rgba(0,114,255,0.40)] overflow-hidden"
                    style={{ background: accentBg ? "linear-gradient(160deg, #EAF3FF 0%, #FFFFFF 100%)" : "linear-gradient(160deg, #FFFFFF 0%, #EAF3FF 100%)" }}
                  >
                    <span aria-hidden="true" className="absolute top-0 left-0 right-0 h-[3px] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500" style={{ background: "linear-gradient(90deg, #0072FF 0%, #1e90ff 60%, #061A3A 100%)" }} />
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ background: accentBg ? "linear-gradient(135deg, #0072FF 0%, #1e90ff 100%)" : "linear-gradient(135deg, #061A3A 0%, #0A2552 100%)", boxShadow: accentBg ? "0 14px 28px -10px rgba(0,114,255,0.55)" : "0 14px 28px -10px rgba(6,26,58,0.55)" }}>
                        <Icon className="w-7 h-7 text-white" strokeWidth={2.2} />
                      </div>
                      <h3 className="font-display-tight text-[22px] lg:text-[24px] font-extrabold text-[var(--swipe-navy)] leading-tight">{c.title}</h3>
                    </div>
                    <p className="mt-5 text-[15px] text-slate-600 leading-[1.75]">{c.desc}</p>

                    <div className="mt-6">
                      <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-[var(--swipe-accent)] mb-2.5">Benefits</div>
                      <ul className="space-y-2">
                        {c.pros.map((p) => (
                          <li key={p} className="flex items-start gap-2.5 text-[14.5px] text-slate-700">
                            <span className="mt-0.5 w-5 h-5 rounded-full bg-[var(--swipe-accent)] text-white flex items-center justify-center shrink-0"><Check className="w-3 h-3" strokeWidth={3.5} /></span>
                            {p}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* PAYMENT METHODS */}
      <section className="bg-[var(--swipe-accent-soft)]/40 border-y border-[var(--swipe-accent)]/15">
        <div className="container-swipe py-20 lg:py-24">
          <Reveal>
            <div className="max-w-3xl mx-auto text-center mb-12">
              <div className="overline mb-3 inline-flex justify-center">Payment Methods</div>
              <h2 className="font-display-tight text-[1.85rem] sm:text-[2.5rem] font-extrabold text-[var(--swipe-navy)] leading-[1.12]">
                Accept every way your customers want to pay.
              </h2>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
            {PAYMENT_METHODS.map((m, i) => {
              const Icon = m.icon;
              const accentBg = i % 2 === 0;
              return (
                <Reveal key={m.title} delay={(i % 3) * 80}>
                  <div
                    data-testid={`pay-method-${i}`}
                    className="group relative h-full rounded-2xl p-7 border border-[var(--swipe-accent)]/15 transition-all duration-300 hover:-translate-y-1.5 hover:border-[var(--swipe-accent)] shadow-[0_10px_30px_-18px_rgba(6,26,58,0.18)] hover:shadow-[0_28px_56px_-22px_rgba(0,114,255,0.40)] overflow-hidden"
                    style={{ background: accentBg ? "linear-gradient(160deg, #FFFFFF 0%, #EAF3FF 100%)" : "linear-gradient(160deg, #EAF3FF 0%, #FFFFFF 100%)" }}
                  >
                    <span aria-hidden="true" className="absolute top-0 left-0 right-0 h-[3px] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500" style={{ background: "linear-gradient(90deg, #0072FF 0%, #1e90ff 60%, #061A3A 100%)" }} />
                    <div className="w-14 h-14 rounded-2xl flex items-center justify-center transition-all duration-300 group-hover:scale-110 group-hover:-rotate-3" style={{ background: accentBg ? "linear-gradient(135deg, #0072FF 0%, #1e90ff 100%)" : "linear-gradient(135deg, #061A3A 0%, #0A2552 100%)", boxShadow: accentBg ? "0 14px 28px -10px rgba(0,114,255,0.55)" : "0 14px 28px -10px rgba(6,26,58,0.55)" }}>
                      <Icon className="w-7 h-7 text-white" strokeWidth={2.2} />
                    </div>
                    <h3 className="mt-6 font-display text-[18px] font-bold text-[var(--swipe-navy)] leading-tight transition-colors duration-300 group-hover:text-[var(--swipe-accent)]">{m.title}</h3>
                    <p className="mt-3 text-[14.5px] text-slate-600 leading-[1.7]">{m.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="section bg-white">
        <div className="container-swipe">
          <Reveal>
            <div className="max-w-3xl mx-auto text-center mb-12">
              <div className="overline mb-3 inline-flex justify-center">Industries We Focus On</div>
              <h2 className="font-display-tight text-[1.85rem] sm:text-[2.5rem] font-extrabold text-[var(--swipe-navy)] leading-[1.12]">
                Built for the way different industries actually run.
              </h2>
              <p className="mt-5 text-[16px] text-slate-600 leading-[1.75] max-w-[820px] mx-auto">
                Our payment solutions are designed to offer flexibility across different business
                environments. From payment terminals to all-in-one POS systems, Swipetronics
                provides processing solutions, support, and training to help businesses accept
                payments efficiently, improve operations, and reduce payment friction.
              </p>
            </div>
          </Reveal>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 lg:gap-5">
            {INDUSTRIES.map((ind, i) => {
              const Icon = ind.icon;
              const accentBg = i % 2 === 0;
              const Wrapper = ind.to ? Link : "div";
              const wrapperProps = ind.to
                ? { to: ind.to, "data-testid": `industry-link-${ind.label.toLowerCase().replace(/\s+/g, "-")}` }
                : {};
              return (
                <Reveal key={ind.label} delay={(i % 5) * 60}>
                  <Wrapper
                    {...wrapperProps}
                    data-testid={ind.to ? undefined : `industry-${i}`}
                    className={`group relative h-full block rounded-2xl p-5 lg:p-6 text-center border border-[var(--swipe-accent)]/15 transition-all duration-300 hover:-translate-y-1.5 hover:border-[var(--swipe-accent)] shadow-[0_8px_22px_-14px_rgba(6,26,58,0.18)] hover:shadow-[0_24px_44px_-20px_rgba(0,114,255,0.40)] overflow-hidden ${ind.to ? "cursor-pointer" : ""}`}
                    style={{ background: accentBg ? "linear-gradient(160deg, #FFFFFF 0%, #EAF3FF 100%)" : "linear-gradient(160deg, #EAF3FF 0%, #FFFFFF 100%)" }}
                  >
                    <div className="w-12 h-12 mx-auto rounded-xl flex items-center justify-center transition-all duration-300 group-hover:scale-110" style={{ background: accentBg ? "linear-gradient(135deg, #0072FF 0%, #1e90ff 100%)" : "linear-gradient(135deg, #061A3A 0%, #0A2552 100%)", boxShadow: accentBg ? "0 10px 22px -8px rgba(0,114,255,0.55)" : "0 10px 22px -8px rgba(6,26,58,0.55)" }}>
                      <Icon className="w-6 h-6 text-white" strokeWidth={2.2} />
                    </div>
                    <div className="mt-4 font-display text-[14px] font-bold text-[var(--swipe-navy)] leading-tight group-hover:text-[var(--swipe-accent)] transition-colors">{ind.label}</div>
                  </Wrapper>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* PAYMENT NETWORKS */}
      <section className="relative bg-[var(--swipe-navy)] text-white overflow-hidden">
        <div className="absolute inset-0 grid-faint opacity-25 pointer-events-none" />
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse 50% 60% at 50% 0%, rgba(0,114,255,0.22) 0%, transparent 65%)" }} />
        <ParticleField density={30} connectDistance={120} className="absolute inset-0 w-full h-full" />

        <div className="container-swipe relative py-20 lg:py-24">
          <Reveal>
            <div className="max-w-3xl mx-auto text-center mb-12">
              <div className="overline mb-3 inline-flex justify-center" style={{ color: "#7DB6FF" }}>Payment Networks</div>
              <h2 className="font-display-tight text-[1.85rem] sm:text-[2.5rem] font-extrabold leading-[1.12] text-white">
                Accept the brands and networks your customers use every day.
              </h2>
              <p className="mt-5 text-[16px] text-white/70 leading-[1.75] max-w-[820px] mx-auto">
                Swipetronics helps businesses accept the payment methods customers prefer. From
                major card brands to digital wallets and electronic payment options, our solutions
                are designed to support secure, flexible payment acceptance wherever your business
                operates.
              </p>
            </div>
          </Reveal>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 lg:gap-5">
            {NETWORKS.map((n, i) => (
              <Reveal key={n} delay={(i % 5) * 50}>
                <div
                  data-testid={`network-${i}`}
                  className="group relative h-full rounded-2xl p-5 lg:p-6 text-center transition-all duration-300 hover:-translate-y-1.5 border border-white/10 backdrop-blur"
                  style={{ background: "rgba(255,255,255,0.04)" }}
                >
                  <span aria-hidden="true" className="absolute top-0 left-0 right-0 h-[3px] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500" style={{ background: "linear-gradient(90deg, #0072FF 0%, #1e90ff 100%)" }} />
                  <div className="w-12 h-12 mx-auto rounded-xl flex items-center justify-center mb-3" style={{ background: "linear-gradient(135deg, #0072FF 0%, #1e90ff 100%)", boxShadow: "0 10px 22px -8px rgba(0,114,255,0.55)" }}>
                    <CreditCard className="w-6 h-6 text-white" strokeWidth={2.2} />
                  </div>
                  <div className="font-display text-[13.5px] font-bold leading-tight" style={{ color: "#FFFFFF" }}>{n}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTASection
        eyebrow="Get a custom quote"
        title="Not sure which service is the right fit?"
        description="Send over your current statement and we'll show you what a switch could look like, side-by-side. No pressure, no obligation."
      />
    </div>
  );
}
